import {
	setupAdInfo,
	setupAdsOnPage,
	updatePageView,
	setupGlobalAdConfig,
	getPageAdConfig,
	getUserConsentPromise,
} from '@/ads/ad-helper';
import {
	prebidSetup,
	addPrebidAdUnits,
	fetchPrebidBids,
	fetchOpenAdsBids,
	maybeRenderBuyItNow,
	getPrebidConfig,
	addOpenAdsAdUnits,
} from '@/ads/prebid-helper';
import { setupGamera } from '@/ads/gamera';
import { gptSetup, syncPageTargetingFromAdInfo } from '@/ads/gpt-helper';
import { parseClientDataStore } from '@/utils/parseClientDataStore';

// Initialize global ad objects
window.googletag = window.googletag || { cmd: [] };
window.pbjs = window.pbjs || {};
window.pbjs.que = window.pbjs.que || [];
window.oajs = window.oajs || {};
window.oajs.que = window.oajs.que || [];
let isInterstitialRendered = false;

// Initialize ad tracking timeline
window.adTimeline = [];
window.logAdEvent = (event) => {
	if (new URLSearchParams(window.location.search).has('debug')) {
		console.debug(`Ad event: ${event}`);
	}
	window.adTimeline.push([performance.now().toFixed(2), event]);
};
document.addEventListener('DOMContentLoaded', () => {
	window.logAdEvent('DOM Ready');
});
window.addEventListener('onAwxAppMounted', () => {
	window.logAdEvent('App mounted');
});

// Custom Raine tracking
const raineAdEventsEnabled = () => {
	const pageFromPath = window.location.pathname.replace('/app', '').replaceAll('/', '');
	return window.tagEngineDataLayer.features?.raineAdsTracking && pageFromPath === 'today';
}

const trackRaineAdEvent = (event, label, value) => {
	if (raineAdEventsEnabled()) {
		window.trackRaineEvent?.(`${window.globalAdConfig?.platform}_ads`, event, label, value);
	}
};

const sendRaineAdEvents = () => {
	if (raineAdEventsEnabled()) {
		window.sendGenericEvents?.(true);
	}
};

// Update page view count for adInfo
updatePageView();

// Setup global variables
const hasAdData = !!window.tagEngineDataLayer.adData || !!window.tagEngineDataLayer.adDataAdc;
if (hasAdData) {
	const pageAdConfig = getPageAdConfig();
	setupGlobalAdConfig(pageAdConfig);
	setupAdInfo(pageAdConfig);
	setupAdsOnPage(pageAdConfig);
	setupGamera(pageAdConfig);
}

window.prebidTimeoutPromise = new Promise((resolve) => {
	if (!hasAdData || window.globalAdConfig.isPrebidDisabled) {
		return Promise.resolve();
	}

	pbjs.que.push(() => {
		pbjs.onEvent('auctionInit', () => {
			setTimeout(resolve, window.globalAdConfig?.prebidTimeout);
		});
	});
});

// Tracks which adUnitCodes have already had a per-slot lazy auction requested,
// so each slot only auctions once per page load.
const lazyAuctionedSlots = new Set();

/**
 * Returns true when per-slot lazy auctions should be used:
 * gptLazyLoading is configured AND gptEnableSingleRequest is disabled.
 */
const isPerSlotLazyAuction = () =>
	!!window.globalAdConfig?.gptLazyLoading &&
	!window.globalAdConfig?.gptEnableSingleRequest;

/**
 * Builds an IntersectionObserver root margin string from fetchMarginPercent.
 * The spec doubles the value so bids are fetched well before GPT's own lazy
 * threshold, giving header bidding enough lead time.
 */
const buildLazyRootMargin = () => {
	const fetchMarginPercent =
		window.globalAdConfig?.gptLazyLoading?.fetchMarginPercent ?? 0;
	const margin = fetchMarginPercent * 2;
	return `${margin}% 0px`;
};

/**
 * Creates a single shared IntersectionObserver that fires per-slot auctions.
 * Called once after refreshAdsOnPage has defined and displayed all GPT slots.
 */
let _lazySlotObserver = null;
const getLazySlotObserver = () => {
	if (_lazySlotObserver) return _lazySlotObserver;

	const rootMargin = buildLazyRootMargin();
	window.logAdEvent(`Lazy auction observer: rootMargin=${rootMargin}`);

	_lazySlotObserver = new IntersectionObserver(
		(entries) => {
			entries.forEach((entry) => {
				if (!entry.isIntersecting) return;

				const adDivId = entry.target.id;
				const adObject = Array.from(window.adsOnPage.values()).find(
					(ad) => ad.adDivId === adDivId,
				);
				if (!adObject) return;

				const { adUnitCode, gptSlot } = adObject;

				// Each slot auctions at most once per page load
				if (lazyAuctionedSlots.has(adUnitCode)) return;
				lazyAuctionedSlots.add(adUnitCode);

				// Stop watching this element
				_lazySlotObserver.unobserve(entry.target);

				window.logAdEvent(`Lazy auction: fetching bids for ${adDivId}`);

				Promise.all([
					fetchPrebidBids([adUnitCode]),
					fetchOpenAdsBids([adUnitCode]),
				]).then(() => {
					if (!gptSlot) return;

					// Apply header-bidding targeting for this specific slot
					const prebidWinners = pbjs.getHighestCpmBids?.(adUnitCode) || [];
					const openAdsWinners = oajs.getHighestCpmBids?.(adUnitCode) || [];
					const allWinners = [...prebidWinners, ...openAdsWinners];

					// Pick the single highest CPM winner across both stacks
					const topWinner = allWinners.reduce(
						(best, bid) => (bid.cpm > (best?.cpm ?? 0) ? bid : best),
						null,
					);

					if (topWinner?.adserverTargeting) {
						const targeting = { ...topWinner.adserverTargeting };

						// Normalise values (mirrors the logic in refreshAdsOnPage)
						for (const [key, value] of Object.entries(targeting)) {
							if (typeof value === 'string' && value.includes(',')) {
								targeting[key] = value.split(',');
							} else if (typeof value === 'number') {
								targeting[key] = String(value);
							}
						}

						// Map Open Ads keys → hb_ keys
						['bidder', 'size', 'pb'].forEach((key) => {
							const oaKey = `oa_${key}`;
							const hbKey = `hb_${key}`;
							if (targeting[oaKey]) targeting[hbKey] = targeting[oaKey];
						});

						window.logAdEvent(
							`Lazy auction: set targeting for ${adDivId} - ${JSON.stringify(targeting)}`,
						);
						gptSlot.setConfig({ targeting });
					}
				});
			});
		},
		{ rootMargin },
	);

	return _lazySlotObserver;
};

/**
 * Tears down the shared lazy observer on page transitions so it is
 * recreated fresh with any updated config on the next page load.
 */
const destroyLazySlotObserver = () => {
	if (_lazySlotObserver) {
		_lazySlotObserver.disconnect();
		_lazySlotObserver = null;
	}
	lazyAuctionedSlots.clear();
};

const fetchInitialAuctions = () => {
	if (isPerSlotLazyAuction()) {
		// In per-slot lazy auction mode, only auction the top and adhesion slots initially
		const initialAdUnitCodes = Array.from(window.adsOnPage.values())
			.filter((ad) => ad.adDivId.includes('top') || ad.adDivId.includes('adhesion'))
			.map((ad) => ad.adUnitCode);
		return Promise.all([fetchPrebidBids(initialAdUnitCodes), fetchOpenAdsBids(initialAdUnitCodes)]);
	} else {
		// Otherwise, auction all slots upfront
		return Promise.all([fetchPrebidBids(), fetchOpenAdsBids()]);
	}
};

// Get user consent before starting ad process
getUserConsentPromise().then(() => {
	if (!hasAdData) return;
	window.logAdEvent('Starting ad process');

	const prebidConfig = getPrebidConfig();

	// Setup Prebid
	pbjs.que.push(() => {
		window.logAdEvent('Prebid: Init');
		prebidSetup(prebidConfig);
		addPrebidAdUnits();
	});

	// Setup Open Ads
	oajs.que.push(() => {
		window.logAdEvent('OpenAds: Init');
		oajs.setConfig({
			...prebidConfig,
			s2sConfig: {
				accountId: 'accuweather',
				bidders: ['thetradedesk'],
				enabled: true,
				endpoint: 'https://openads.adsrvr.org/openrtb2/auction',
				syncEndpoint: 'https://openads.adsrvr.org/cookie_sync',
				timeout: window.globalAdConfig.prebidTimeout,

			},
		});
		addOpenAdsAdUnits();
	})

	// Start header bidding auctions
	window.initialAuctionsPromise = fetchInitialAuctions();

	// Send to ad server
	googletag.cmd.push(() => {
		window.logAdEvent('GPT: Init');
		gptSetup();

		// Raine ads tracking
		googletag.pubads().addEventListener('slotResponseReceived', (event) => {
			const adDivId = event.slot.getSlotElementId();
			if (adDivId.includes('top')) {
				const topAdEl = document.getElementById(adDivId);
				const rect = topAdEl.getBoundingClientRect();
				const styles = window.getComputedStyle(topAdEl);

				trackRaineAdEvent('top_ad_slotResponseReceived');
				trackRaineAdEvent('top_ad_propsOnRespReceived', 'divSize', `${rect.width}x${rect.height}`);
				trackRaineAdEvent('top_ad_propsOnRespReceived', 'divDisplay', styles.display);
				trackRaineAdEvent('top_ad_propsOnRespReceived', 'divVisibility', styles.visibility);
				trackRaineAdEvent('top_ad_propsOnRespReceived', 'divOpacity', styles.opacity);
				trackRaineAdEvent('top_ad_propsOnRespReceived', 'hasHiddenAncestor', `${hasHiddenAncestor(topAdEl)}`);
				trackRaineAdEvent('top_ad_propsOnRespReceived', 'visibilityState', document.visibilityState);

				sendRaineAdEvents();
			}
		});
		googletag.pubads().addEventListener('slotRenderEnded', (event) => {
			const adDivId = event.slot.getSlotElementId();
			if (adDivId.includes('top')) {
				trackRaineAdEvent('top_ad_slotRenderEnded');
				trackRaineAdEvent('top_ad_propsOnRenderEnded', 'isEmpty', `${event.isEmpty}`);
				trackRaineAdEvent('top_ad_propsOnRenderEnded', 'visibilityState', document.visibilityState);

				const topAdEl = document.getElementById(adDivId);
				if (!topAdEl) {
					trackRaineAdEvent('top_ad_propsOnRenderEnded', 'divNotFound');
				} else {
					const rect = topAdEl.getBoundingClientRect();
					trackRaineAdEvent('top_ad_propsOnRenderEnded', 'divSize', `${rect.width}x${rect.height}`);
				}

				sendRaineAdEvents();
			}
		});

		let isAdServerInitialized = false;
		const initAdserver = () => {
			if (isAdServerInitialized) {
				return;
			}
			isAdServerInitialized = true;

			const isAppMounted = document.querySelector('.app.loaded');
			const isAdc = window.tagEngineDataLayer?.adDataAdc;
			if (isAppMounted || isAdc) {
				refreshAdsOnPage();
			} else {
				window.addEventListener('onAwxAppMounted', refreshAdsOnPage);
			}
		};

		// If initial ad load is disabled, wait for prebid
		if (window.globalAdConfig.disableInitialAdLoad) {
			// A timeout incase bidding fails or takes too long
			setTimeout(initAdserver, window.globalAdConfig.awxTimeout);
			// Wait for both prebid and open ads bids
			window.initialAuctionsPromise.then(initAdserver);
		} else {
			initAdserver();
		}

		googletag.pubads().addEventListener('slotResponseReceived', () => {
			if (!isInterstitialRendered && window.adsOnPage.has('interstitial')) {
				isInterstitialRendered = true;
				const { gptSlot } = window.adsOnPage.get('interstitial');
				if (gptSlot) {
					googletag.display(gptSlot.getSlotElementId());
					googletag.pubads().refresh([gptSlot]);
				}
			}
		});
	});
});

const runReloadAds = (pageId, isTransient) => {
	window.dispatchEvent(new CustomEvent('adsReload'));

	const canonicalUrl = document.querySelector('link[rel="canonical"]')?.href || `${window.location.protocol}//${window.location.host}${window.location.pathname}`;
	const updatedConfig = { ortb2: { site: { page: canonicalUrl, content: { url: canonicalUrl, title: document.title } } } };
	pbjs.mergeConfig?.(updatedConfig);
	oajs.mergeConfig?.(updatedConfig);

	// Clear header bidding ad units
	pbjs.removeAdUnit?.();
	oajs.removeAdUnit?.();

	const _adData = getPageAdConfig(pageId);
	if (_adData?.adSlots) {
		isInterstitialRendered = false;

		// Tear down the lazy observer so it is rebuilt fresh for the new page
		destroyLazySlotObserver();

		setupAdInfo(_adData);
		googletag.cmd.push(syncPageTargetingFromAdInfo);
		setupAdsOnPage(_adData);
		addPrebidAdUnits();
		addOpenAdsAdUnits();
		fetchInitialAuctions().then(() => {
			if (isTransient) {
				// destroying all slots for transient page
				const allSlots = googletag.pubads().getSlots();
				const slotsToDestroy = allSlots.filter(slot =>
					Object.keys(_adData.adSlots).includes(slot.getSlotElementId())
				);
				if (slotsToDestroy.length > 0) googletag.destroySlots(slotsToDestroy);
			} else {
				// destroying all slots for full page load
				googletag.destroySlots();
			}
			refreshAdsOnPage();
		});
	}
};

// Debounced reloadAds
let reloadAdsTimerId;
window.reloadAds = (pageId, isTransient) => {
	if (reloadAdsTimerId) {
		clearTimeout(reloadAdsTimerId);
	}
	reloadAdsTimerId = setTimeout(() => {
		reloadAdsTimerId = undefined;
		runReloadAds(pageId, isTransient);
	}, 100);
};

const refreshAdsOnPage = () => {
	window.dispatchEvent(new CustomEvent('refreshAdsOnPage'));

	const pageFromPath = window.location.pathname.toLowerCase().replace('/app', '').replaceAll('/', '');
	const isTodayPage = pageFromPath === 'today';
	const shouldSplitTodaySraLazyLoad =
		isTodayPage &&
		window.globalAdConfig.gptEnableSingleRequest &&
		!!window.globalAdConfig.gptLazyLoading;

	const primarySlotsToRefresh = [];
	const secondarySlotsToRefresh = [];

	window.adsOnPage.forEach((adObject) => {
		if (!document.getElementById(adObject?.adDivId)) {
			window.logAdEvent(`Unable to find ad div for: ${adObject?.adDivId}`);

			if (adObject?.adDivId?.includes('top')) {
				trackRaineAdEvent('top_ad_slot_not_found_before_display');
				sendRaineAdEvents();
			}
			return;
		}

		const { sizes, adDivId, slot } = adObject;

		// If BIN wins, render and track via tagless request, and skip GPT slot
		const binHandled = maybeRenderBuyItNow(adObject);
		if (binHandled) {
			return;
		}

		let gptSlot;

		if (slot === 'oop') {
			gptSlot = googletag.defineOutOfPageSlot(adObject.adUnitCode, adDivId);
		} else if (slot === 'interstitial') {
			gptSlot = googletag.defineOutOfPageSlot(
				adObject.adUnitCode,
				googletag.enums.OutOfPageFormat.INTERSTITIAL,
			);
		} else {
			gptSlot = googletag.defineSlot(adObject.adUnitCode, sizes.filter(s => s !== 'video'), adDivId);
		}

		if (gptSlot) {
			// Add to adObject
			adObject.gptSlot = gptSlot;

			// Add service
			gptSlot.addService(googletag.pubads());

			// Display slot
			window.logAdEvent(`GPT: Display: ${adDivId}`);
			googletag.display(gptSlot);

			// Set UPR targeting
			for (const [key, value] of Object.entries(
				window.getUprTargeting(adObject),
			)) {
				if (value) {
					gptSlot.setTargeting(key, String(value));
				}
			}

			// Set Gamera targeting
			try {
				window.gamera
					._getAdPlacements()
					.forEach(({ divId, universalPlacementId }) => {
						if (divId === adObject.adDivId && universalPlacementId) {
							gptSlot.setTargeting('upid', universalPlacementId);
						}
					});
			} catch {
				// noop
			}

			// Observe for per-slot lazy auction if enabled and slot is eligible
			if (isPerSlotLazyAuction()) {
				if (!['interstitial', 'preroll', 'oop', 'adhesion', 'top'].includes(slot)) {
					const el = document.getElementById(adDivId);
					if (el) {
						getLazySlotObserver().observe(el);
						window.logAdEvent(`Lazy auction: observing ${adDivId}`);
					}
				}
			}

			// Include in refresh groups
			if (slot !== 'interstitial' && slot !== 'preroll') {
				const isSecondaryTodaySlot =
					shouldSplitTodaySraLazyLoad &&
					(adDivId?.includes('middle') || adDivId?.includes('bottom'));

				if (isSecondaryTodaySlot) {
					secondarySlotsToRefresh.push(gptSlot);
				} else {
					primarySlotsToRefresh.push(gptSlot);
				}
			}
		}
	});

	// Set gpt ad units targeting from header bidding winners
	const prebidWinners = pbjs.getHighestCpmBids?.() || [];
	const openAdsWinners = oajs.getHighestCpmBids?.() || [];
	const allWinners = [...prebidWinners, ...openAdsWinners];
	const winningBidsMap = new Map();
	allWinners.forEach((bid) => {
		const existingBid = winningBidsMap.get(bid.adUnitCode);
		if (!existingBid || bid.cpm > existingBid.cpm) {
			winningBidsMap.set(bid.adUnitCode, bid);
		}
	});
	winningBidsMap.forEach((bid) => {
		const adObject = Array.from(window.adsOnPage.values()).find(
			(ad) => ad.adUnitCode === bid.adUnitCode,
		);
		if (adObject?.gptSlot) {
			const { adserverTargeting } = bid;

			// Normalize targeting values:
			// if a string contains commas, turn it into an array
			// if a number, turn it into a string
			for (const [key, value] of Object.entries(adserverTargeting)) {
				if (typeof value === 'string' && value.includes(',')) {
					adserverTargeting[key] = value.split(',');
				} else if (typeof value === 'number') {
					adserverTargeting[key] = String(value);
				}
			}

			// Map Open Ads keys to header bidding keys
			['bidder', 'size', 'pb'].forEach((key) => {
				const oaKey = `oa_${key}`;
				const hbKey = `hb_${key}`;
				if (adserverTargeting[oaKey]) {
					adserverTargeting[hbKey] = adserverTargeting[oaKey];
				}
			});
			window.logAdEvent(`GPT: Set targeting for ${adObject.adDivId} - ${JSON.stringify(adserverTargeting)}`);
			adObject.gptSlot.setConfig({ targeting: adserverTargeting });
		}
	});

	// Only call refresh if initial load was disabled
	if (window.globalAdConfig.disableInitialAdLoad && document.visibilityState === 'visible') {
		window.logAdEvent(`GPT: Refresh`);

		if (shouldSplitTodaySraLazyLoad) {
			if (primarySlotsToRefresh.length) {
				googletag.pubads().refresh(primarySlotsToRefresh);
			}

			if (secondarySlotsToRefresh.length) {
				googletag.pubads().refresh(secondarySlotsToRefresh);
			}
		} else if (window.globalAdConfig.gptEnableSingleRequest) {
			googletag.pubads().refresh(primarySlotsToRefresh);
		} else {
			primarySlotsToRefresh.forEach((gptSlot) => {
				googletag.pubads().refresh([gptSlot]);
			});
		}
	}
};

function hasHiddenAncestor(el) {
	while (el) {
		const s = window.getComputedStyle(el);
		if (s.display === 'none' || s.visibility === 'hidden' || s.opacity === '0') {
			return true;
		}
		el = el.parentElement;
	}
	return false;
}
